import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { callRpc, useAgencyId, useLiveQuery, type ActionResult, type LiveState } from './rpc';

/**
 * The caller's own mutes of the Activity bell (change spec 2026-10-02/activity-feed, design 3.3 and 9.2):
 * the `activity_mutes` rows RLS lets him read (his own, self only), and `save(groups)` through
 * `activity_prefs_set`, which replaces the whole set for the current agency. A muted group does not ring
 * the bell or count as unread; the Activity page still lists it.
 */
export interface UseActivityPrefs {
  status: LiveState<string[]>['status'];
  muted: string[];
  save(groups: string[]): Promise<ActionResult<string[]>>;
}

export function useActivityPrefs(enabled: boolean): UseActivityPrefs {
  const { client, user } = useAuth();
  const agencyId = useAgencyId();
  const userId = user?.id ?? null;
  const state = useLiveQuery<string[]>(
    async (c, agency) => {
      if (!enabled || !userId) return { data: [] };
      const { data, error } = await c.from('activity_mutes').select('kind_group').eq('agency_id', agency).eq('user_id', userId);
      if (error) return { error: { code: error.code, message: error.message } };
      return { data: ((data ?? []) as { kind_group: string }[]).map((r) => r.kind_group).sort() };
    },
    [enabled, userId],
  );
  const { reload } = state;
  const save = React.useCallback(
    async (groups: string[]) => {
      if (!agencyId) return { data: null, error: { code: undefined, message: 'no agency selected' } };
      const res = await callRpc<string[]>(client, 'activity_prefs_set', { p_agency: agencyId, p_muted: [...new Set(groups)].sort() });
      if (!res.error) reload();
      return res;
    },
    [client, agencyId, reload],
  );
  return { status: state.status, muted: state.status === 'ready' ? state.data : [], save };
}

/**
 * The master switch of the Activity bell (change spec 2026-10-03/activity-master-switch): the caller's own
 * `activity_settings` row for the current agency (RLS self only; no row = on), and `set(on)` through
 * `activity_notify_set`. Off: the server counts no Activity unread and pings nobody about it, for every
 * kind group; the history and the per-group mutes are untouched.
 *
 * The boolean the RPC returns is the stored state: it is applied at once (here and, through a window event,
 * in every other mounted copy) instead of waiting for a re-read, so no control ever shows the old state
 * after the server answered (the PR 761 race: a second click sent the same value again). `ready` is false
 * while the read is loading, refreshing or failed and while a set is in flight; the controls are disabled
 * then. A failed read counts as ON for the badge (the portal never hides a count the server did not
 * silence) but the switch cannot be flipped.
 */
export interface UseActivityNotify {
  status: LiveState<boolean>['status'];
  notify: boolean;
  /** The stored state is known and nothing is in flight: the controls may act. */
  ready: boolean;
  set(on: boolean): Promise<ActionResult<boolean>>;
}

const NOTIFY_EVENT = 'coreway:activity-notify';
type NotifyDetail = { agencyId: string; userId: string; notify: boolean };

export function useActivityNotify(enabled: boolean): UseActivityNotify {
  const { client, user } = useAuth();
  const agencyId = useAgencyId();
  const userId = user?.id ?? null;
  const state = useLiveQuery<boolean>(
    async (c, agency) => {
      if (!enabled || !userId) return { data: true };
      const { data, error } = await c.from('activity_settings').select('notify').eq('agency_id', agency).eq('user_id', userId);
      if (error) return { error: { code: error.code, message: error.message } };
      const row = ((data ?? []) as { notify: boolean }[])[0];
      return { data: row ? row.notify !== false : true };
    },
    [enabled, userId],
  );
  // The server's answer to the last set, for this agency and user; it wins over an older read.
  const [applied, setApplied] = React.useState<NotifyDetail | null>(null);
  const [pending, setPending] = React.useState(false);
  // A new query (another agency, sign-in) starts from loading: its own read is the truth again.
  React.useEffect(() => {
    if (state.status === 'loading') setApplied(null);
  }, [state.status]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onEvent = (e: Event) => {
      const d = (e as CustomEvent<NotifyDetail>).detail;
      if (d && d.agencyId === agencyId && d.userId === userId) setApplied(d);
    };
    window.addEventListener(NOTIFY_EVENT, onEvent);
    return () => window.removeEventListener(NOTIFY_EVENT, onEvent);
  }, [agencyId, userId]);

  const set = React.useCallback(
    async (on: boolean) => {
      if (!agencyId || !userId) return { data: null, error: { code: undefined, message: 'no agency selected' } };
      setPending(true);
      try {
        const res = await callRpc<boolean>(client, 'activity_notify_set', { p_agency: agencyId, p_on: on });
        if (!res.error && typeof window !== 'undefined') {
          const detail: NotifyDetail = { agencyId, userId, notify: res.data !== false };
          window.dispatchEvent(new CustomEvent<NotifyDetail>(NOTIFY_EVENT, { detail }));
        }
        return res;
      } finally {
        setPending(false);
      }
    },
    [client, agencyId, userId],
  );

  const own = applied && applied.agencyId === agencyId && applied.userId === userId ? applied.notify : undefined;
  const notify = own ?? (state.status === 'ready' ? state.data : true);
  const ready = state.status === 'ready' && (own !== undefined || !state.refreshing) && !pending;
  return { status: state.status, notify, ready, set };
}
